import { For, Show, createMemo, createSignal, type Component, type JSX } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Text } from '~ui/text';
import { Button } from '~ui/button';
import { Divider } from '~ui/divider';
import { Table, type Column } from '~ui/table';
import { NA, formatCurrency, formatDay, formatNumber, formatPercent, roundLargeNumber, todayISO } from '~/lib/format';
import {
    annualDistribution,
    assets,
    distributionFrequency,
    distributions,
    fund,
    instrumentYield,
    issuerOf,
    managementFee,
    mandateOf,
    name,
    price,
    returnOfCapitalShare,
    riskRating,
} from '~data';
import { CloseChart } from '~/features/close-chart';
import { OverlayChart } from '~/features/overlay-chart';
import { CompareBreakdown } from '~/features/compare-breakdown';
import { ComparePerformance } from '~/features/compare-performance';
import { Swatch } from '~/features/swatch';
import { FundPicker } from '~/features/fund-picker';
import { CrossIcon, PlusIcon } from '~ui/icons';
import type { Axis } from '~/stores/exposure-store';
import { records } from '~/stores/history-store';
import { resolved } from '~/stores/portfolio-store';
import { clear, drawn, isSelected, selected, toggle } from '~/stores/compare-store';
import { BASIS_LABEL, type Basis } from '~/stores/series-store';

const ASOF = todayISO();

const signed = (n: number | null): 'green' | 'red' | undefined => (n == null ? undefined : n >= 0 ? 'green' : 'red');

// Rows are the measures and columns are the funds — the transpose of every other
// table in the app, and the right way round here. Comparing five funds on twenty
// measures means reading across one measure at a time, which only works if the
// measure is the row.
interface Metric {
    label: string;
    cell: (symbol: string) => JSX.Element;
}

const recordFor = (symbol: string) => records().find((r) => r.symbol === symbol);

/**
 * The payment record: how many, and how often the rate moved which way.
 *
 * Cuts alone would be a one-sided number — AMAX has cut four times and raised
 * eighteen, and a column showing only the four says the opposite of what
 * happened. Counted from the rate changing between consecutive payments, so a
 * fund that holds flat for a year contributes nothing to either.
 */
const paymentRecord = (symbol: string): { count: number; cuts: number; raises: number } => {
    const paid = [...distributions(symbol)].map((d) => ({ t: d.payDate ?? d.exDate, amount: d.amount })).sort((a, b) => a.t.localeCompare(b.t));
    let cuts = 0;
    let raises = 0;
    for (let i = 1; i < paid.length; i += 1) {
        if (paid[i].amount < paid[i - 1].amount) cuts += 1;
        else if (paid[i].amount > paid[i - 1].amount) raises += 1;
    }
    return { count: paid.length, cuts, raises };
};

const heldUnits = (symbol: string): number =>
    resolved()
        .filter((r) => r.symbol === symbol)
        .reduce((sum, r) => sum + r.holding.units, 0);

const METRICS: Metric[] = [
    { label: 'Name', cell: (s) => <Text class='truncate'>{name(s) ?? NA}</Text> },
    { label: 'Price', cell: (s) => <Text weight='medium'>{formatCurrency(price(s))}</Text> },
    { label: 'Yield', cell: (s) => formatPercent(instrumentYield(s, ASOF)) },
    { label: 'Distribution / yr', cell: (s) => <Text tone='green'>{formatCurrency(annualDistribution(s, ASOF))}</Text> },
    {
        label: 'Distribution / mo',
        cell: (s) => {
            const annual = annualDistribution(s, ASOF);
            return <Text tone='green'>{formatCurrency(annual == null ? null : annual / 12)}</Text>;
        },
    },
    { label: 'Payments made', cell: (s) => formatNumber(paymentRecord(s).count) },
    {
        label: 'Times reduced',
        cell: (s) => {
            const cuts = paymentRecord(s).cuts;
            return <Text tone={cuts > 0 ? 'red' : 'muted'}>{formatNumber(cuts)}</Text>;
        },
    },
    {
        label: 'Times raised',
        cell: (s) => {
            const raises = paymentRecord(s).raises;
            return <Text tone={raises > 0 ? 'green' : 'muted'}>{formatNumber(raises)}</Text>;
        },
    },
    { label: 'Return of capital', cell: (s) => formatPercent(returnOfCapitalShare(s), 0) },
    { label: 'Management fee', cell: (s) => formatPercent(managementFee(s)) },
    { label: 'Frequency', cell: (s) => <Text class='truncate'>{distributionFrequency(s) ?? NA}</Text> },
    { label: 'Risk', cell: (s) => <Text class='truncate'>{riskRating(s) ?? NA}</Text> },
    { label: 'Mandate', cell: (s) => <Text class='truncate'>{mandateOf(s) ?? NA}</Text> },
    { label: 'Issuer', cell: (s) => <Text class='truncate'>{issuerOf(s) ?? NA}</Text> },
    { label: 'Assets', cell: (s) => (assets(s) == null ? NA : `$${roundLargeNumber(assets(s))}`) },
    { label: 'Inception', cell: (s) => <Text tone='muted'>{fund(s)?.inceptionDate ? formatDay(fund(s)!.inceptionDate!) : NA}</Text> },

    // The record, from history-store — the same window and the same arithmetic
    // the History page reports, so the two can never disagree.
    { label: 'History from', cell: (s) => <Text tone='muted'>{recordFor(s) ? formatDay(recordFor(s)!.from) : NA}</Text> },
    {
        label: 'Capital',
        cell: (s) => <Text tone={signed(recordFor(s)?.capitalPct ?? null)}>{formatPercent(recordFor(s)?.capitalPct ?? null, 1)}</Text>,
    },
    { label: 'Income', cell: (s) => <Text tone='green'>{formatPercent(recordFor(s)?.incomePct ?? null, 1)}</Text> },
    {
        label: 'Total return',
        cell: (s) => (
            <Text tone={signed(recordFor(s)?.totalPct ?? null)} weight='medium'>
                {formatPercent(recordFor(s)?.totalPct ?? null, 1)}
            </Text>
        ),
    },
    {
        label: 'Was your own money',
        cell: (s) => {
            const share = recordFor(s)?.erosionShare ?? null;
            return <Text tone={share == null ? 'muted' : share >= 33 ? 'red' : undefined}>{formatPercent(share, 0)}</Text>;
        },
    },

    // The join back to the book, so the page answers "and which of these do I
    // already own" without leaving it.
    {
        label: 'Units held',
        cell: (s) => <Text tone={heldUnits(s) > 0 ? undefined : 'muted'}>{heldUnits(s) > 0 ? formatNumber(heldUnits(s), { maximumFractionDigits: 0 }) : '—'}</Text>,
    },
];

type ChartView = 'separate' | 'overlay';

// What the lower half shows. Metrics is the fund against itself; the three axes
// are what it is made of. All four obey the Separate/Overlay toggle above, which
// means the same thing throughout the page: each fund on its own terms, or every
// fund read against the same rows.
type Panel = 'metrics' | 'yearly' | 'monthly' | 'overlap' | Axis;

const PANELS: { id: Panel; label: string }[] = [
    { id: 'metrics', label: 'Metrics' },
    // Period returns sit next to Metrics rather than with the breakdowns: they
    // are about what a fund did, like everything in Metrics, where the three
    // axes below are about what it is made of.
    { id: 'yearly', label: 'By year' },
    { id: 'monthly', label: 'By month' },
    // Overlap leads the three axes: "how much of these two is the same thing"
    // is the question a comparison is usually for, and it is a filtered view of
    // the company axis rather than an axis of its own.
    { id: 'overlap', label: 'Overlap' },
    { id: 'company', label: 'Company' },
    { id: 'sector', label: 'Sector' },
    { id: 'geography', label: 'Geography' },
];

const Compare: Component = () => {
    // Two honest ways to show these, answering different questions, which is why
    // it is a toggle rather than a default.
    //
    //   Separate  each fund on its own scale, full history, absolute dollars.
    //             What did this fund do.
    //   Overlay   one plot, indexed to 100 at the first shared session.
    //             Which of these did better, over the window they share.
    //
    // Neither is a second y-axis, which is the thing that would actually lie.
    const [chart, setChart] = createSignal<ChartView>('overlay');

    // What the overlay plots. Only meaningful there: the separate charts are
    // each fund's own price on its own scale, which is the NAV question already.
    const [basis, setBasis] = createSignal<Basis>('nav');
    const [panel, setPanel] = createSignal<Panel>('metrics');
    const columns = createMemo<Column<Metric>[]>(() => [
        { id: 'label', header: '', size: 1.1, cell: (m) => <Text tone='muted'>{m().label}</Text> },
        ...selected().map(
            (symbol): Column<Metric> => ({
                id: symbol,
                header: symbol,
                align: 'right',
                size: 1,
                cell: (m) => m().cell(symbol),
            }),
        ),
    ]);

    return (
        <Panel>
            <Toolbar>
                <ToolbarSection class='min-w-0 flex-1 gap-1 overflow-x-auto scrollbar-hide'>
                    {/* A search box and a chip per selection, rather than a
                        button per fund. The full row was fine at eighteen and is
                        not a design — it is a list that happens to fit, and it
                        stopped fitting the moment the covered pool grew. */}
                    <div class='flex h-full shrink-0 items-center'>
                        <FundPicker symbol='' placeholder='Add a fund' icon={<PlusIcon />} omit={selected()} onSelect={(symbol) => !isSelected(symbol) && toggle(symbol)} />
                    </div>

                    <For each={drawn()}>
                        {(f) => (
                            <Button size='fill' class='shrink-0' title={`Remove ${name(f.symbol) ?? f.symbol}`} onClick={() => toggle(f.symbol)}>
                                {/* The fund's own mark, on the chip that carries
                                    it. With a dozen on the plot, matching a line
                                    to a name is the whole difficulty. */}
                                <Swatch color={f.color} dash={f.dash} />
                                {f.symbol}
                                <span class='text-muted-foreground'>
                                    <CrossIcon />
                                </span>
                            </Button>
                        )}
                    </For>
                </ToolbarSection>
                <ToolbarSection>
                    {/* Shown wherever it changes something: the overlay chart,
                        and the two period tables below, which read the same
                        signal. Hidden on the separate charts, where each fund is
                        on its own price scale and there is no basis to pick. */}
                    <Show when={chart() === 'overlay' || panel() === 'yearly' || panel() === 'monthly'}>
                        <For each={['nav', 'cash', 'drip'] as Basis[]}>
                            {(b) => (
                                <Button
                                    size='fill'
                                    label={BASIS_LABEL[b]}
                                    class={basis() === b ? 'bg-muted-foreground/20 border-b-3 border-foreground hover:bg-muted-foreground/20!' : ''}
                                    onClick={() => setBasis(b)}
                                />
                            )}
                        </For>
                        <Divider />
                    </Show>
                    <Button
                        size='fill'
                        label='Separate'
                        class={chart() === 'separate' ? 'bg-muted-foreground/20 border-b-3 border-foreground hover:bg-muted-foreground/20!' : ''}
                        title='One chart per fund, each on its own scale'
                        onClick={() => setChart('separate')}
                    />
                    <Button
                        size='fill'
                        label='Overlay'
                        class={chart() === 'overlay' ? 'bg-muted-foreground/20 border-b-3 border-foreground hover:bg-muted-foreground/20!' : ''}
                        title='One chart, indexed to 100 at the first shared session'
                        onClick={() => setChart('overlay')}
                    />
                    <Divider />
                    <Button size='fill' label='Clear' title='Deselect every fund' disabled={selected().length === 0} onClick={clear} />
                </ToolbarSection>
            </Toolbar>

            <Show
                when={selected().length > 0}
                fallback={
                    <div class='flex flex-1 items-center justify-center'>
                        <Text tone='muted'>Pick two or more funds above</Text>
                    </div>
                }>
                <Show
                    when={chart() === 'separate'}
                    fallback={
                        <div class='shrink-0 border-b'>
                            <OverlayChart series={drawn()} basis={basis()} />
                        </div>
                    }>
                    <div class='flex shrink-0 gap-px bg-border'>
                        <For each={selected()}>
                            {(symbol) => (
                                <div class='min-w-0 flex-1 bg-background'>
                                    <div class='flex h-row items-center justify-between gap-2 border-b px-2'>
                                        <Text weight='medium'>{symbol}</Text>
                                        <Text tone='muted' class='truncate'>
                                            {mandateOf(symbol) ?? ''}
                                        </Text>
                                    </div>
                                    <CloseChart symbol={symbol} height='h-24' />
                                </div>
                            )}
                        </For>
                    </div>
                </Show>

                <div class='flex h-row shrink-0 items-center border-b'>
                    <For each={PANELS}>
                        {(p) => (
                            <Button
                                size='fill'
                                label={p.label}
                                class={panel() === p.id ? 'bg-muted-foreground/20 border-b-3 border-foreground hover:bg-muted-foreground/20!' : ''}
                                onClick={() => setPanel(p.id)}
                            />
                        )}
                    </For>
                </div>

                <Show when={panel() === 'metrics'}>
                    <Table columns={columns()} rows={METRICS} empty='Nothing to compare' />
                </Show>
                {/* The basis toggle above drives these too, so "by year" means
                    price, price plus cash, or reinvested — whichever is selected
                    for the chart. One control, one meaning across the page. */}
                <Show when={panel() === 'yearly' || panel() === 'monthly'}>
                    <ComparePerformance grain={panel() === 'yearly' ? 'year' : 'month'} basis={basis()} />
                </Show>
                <Show when={panel() === 'overlap'}>
                    <CompareBreakdown axis='company' overlay shared />
                </Show>
                <Show when={panel() !== 'metrics' && panel() !== 'yearly' && panel() !== 'monthly' && panel() !== 'overlap'}>
                    <CompareBreakdown axis={panel() as Axis} overlay={chart() === 'overlay'} />
                </Show>
            </Show>
        </Panel>
    );
};

export default Compare;
